import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useSyncExternalStore,
  ReactNode,
} from 'react';
import { googleLogout } from '@react-oauth/google';
import { decodeJwt } from './token';
import { getAccess, subscribe, clear } from './tokenStore';
import { loginWithGoogle, refreshTokens, AuthRejectedError } from './authApi';

type Status = 'loading' | 'authed' | 'anon';

interface AuthValue {
  status: Status;
  email: string | null;
  authError: string | null;
  signInWithGoogle: (idToken: string) => Promise<void>;
  signOut: () => void;
}

const AuthCtx = createContext<AuthValue | undefined>(undefined);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Re-render whenever the in-memory access token changes (login / refresh / clear).
  const access = useSyncExternalStore(subscribe, getAccess, getAccess);
  const [status, setStatus] = useState<Status>('loading');
  const [authError, setAuthError] = useState<string | null>(null);

  // Boot: try to rehydrate a session from a stored refresh token. On a fresh tab
  // (refresh in sessionStorage, access gone) this mints a new access token; with no
  // refresh token it resolves to anon. status stays 'loading' until it settles, so
  // LoginGate never flashes the sign-in screen mid-check.
  useEffect(() => {
    let cancelled = false;
    refreshTokens().then((ok) => {
      if (!cancelled) setStatus(ok ? 'authed' : 'anon');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithGoogle = useCallback(async (idToken: string) => {
    try {
      await loginWithGoogle(idToken);
      setAuthError(null);
      setStatus('authed');
    } catch (err) {
      googleLogout(); // forget the rejected account so the next prompt re-asks
      setAuthError(
        err instanceof AuthRejectedError
          ? "That account isn't authorised — only the practice owner can sign in."
          : 'Sign-in failed. Please try again.',
      );
    }
  }, []);

  const signOut = useCallback(() => {
    clear();
    googleLogout();
    setStatus('anon');
  }, []);

  let email: string | null = null;
  if (access) {
    try {
      email = decodeJwt(access).email ?? null;
    } catch {
      email = null;
    }
  }

  return (
    <AuthCtx.Provider value={{ status, email, authError, signInWithGoogle, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}
